/**
 * DASHBOARD-NPS-BENCHMARKS
 * Compares this account's true eNPS index (-100..100, promoters% - detractors%) to its sector
 * benchmark — both the mini card and the detail modal it opens use the real NPS index (the
 * 'nps' type calc_benchmarks row), not the underlying question's plain average.
 *
 * npsEntry/isLoading sont fetchés une seule fois par le parent (dashboard-nps.widget.tsx) et
 * passés en props : le fetch benchmarkFetchNps ne prend pas de paramètre par question (l'index
 * est global au compte), donc le refaire ici par instance serait redondant — un par question NPS
 * affichée sur la page (un compte avec beaucoup de questions taguées NPS multipliait ce fetch
 * d'autant, ralentissant tout le chargement de la page).
 */

import { useState } from "react"
import { useAppSelector } from "@/core/root-reducer"
import type { NpsBenchmarkEntry } from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"
import { benchmarkDelta } from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"
import DashboardBenchmarkMiniCard from "@/features/benchmark/entities/question/ui/DashboardBenchmarkMiniCard"
import BenchmarkDetailModal from "@/features/benchmark/entities/question/ui/BenchmarkDetailModal"

// NPS raw units are -100..100 vs. 0-10 for scale questions — 0.3 (the question threshold)
// would flag almost any non-tied comparison as above/below average.
const NPS_STEP_THRESHOLD = 5

interface OwnProps {
  npsScore: number | null
  questionLabel: string
  topicLabel: string
  axisColor: string
  npsEntry: NpsBenchmarkEntry | null
  isLoading: boolean
}

export default function DashboardNpsBenchmarksWidget({
  npsScore,
  questionLabel,
  topicLabel,
  axisColor,
  npsEntry,
  isLoading
}: OwnProps) {
  const accountName = useAppSelector((state) => state._session.accountName)
  const accountOptions = useAppSelector((state) => state._session.dashboardDisplaySettings)

  const [showModal, setShowModal] = useState(false)

  if (!isLoading && !npsEntry) return null

  const score = npsScore ?? 0

  return (
    <>
      <DashboardBenchmarkMiniCard
        isLoading={isLoading}
        accountOptions={accountOptions}
        score={score}
        benchmarkValue={npsEntry ? npsEntry.value : null}
        stepThreshold={NPS_STEP_THRESHOLD}
        onOpen={() => setShowModal(true)}
      />

      {showModal && npsEntry && (
        <BenchmarkDetailModal
          accountName={accountName}
          accountOptions={accountOptions}
          score={score}
          axisColor={axisColor}
          questionAid="nps"
          benchmarkEntry={{
            value: npsEntry.value,
            count: npsEntry.count,
            canonicalAid: "nps",
            sector: npsEntry.sector
          }}
          sector={npsEntry.sector}
          domain="nps"
          distributionOverride={npsEntry.distribution}
          navItems={[
            {
              label: questionLabel,
              questionAid: "nps",
              topicLabel,
              axisColor,
              delta: benchmarkDelta(score, npsEntry.value, 1, 0)
            }
          ]}
          navCurrentIndex={0}
          onNavigate={() => {}}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}
